/* LegalLayout — shared chrome for /privacy, /cookies, /legal/dpa,
 * /legal/subprocessors.
 *
 * Renders the same MarketingNav + Footer used on /pricing so legal
 * pages sit visually inside the same product site rather than feeling
 * like a separate microsite. Includes a sticky table-of-contents on
 * the left so prospects can jump to a clause without scrolling.
 *
 * NOTE TO MAINTAINERS: the body text of each legal page is a starting
 * template. Have a UK-qualified solicitor review before any change
 * material to the privacy practices. Last reviewed: Feb 2026.
 */
import { Link } from 'react-router-dom';
import { Button } from '../ui/button';
import { HyreDocsIcon } from '../HyreDocsLogo';

const DEMO_MAILTO = 'mailto:hello@hyredocs.com?subject=HyreDocs%20Demo%20Request';

function MarketingNav() {
  return (
    <nav className="w-full border-b border-slate-200 bg-white/80 backdrop-blur-md sticky top-0 z-40" data-testid="legal-nav">
      <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2.5">
          <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-[#f97316] to-[#f59e0b] flex items-center justify-center shadow-sm">
            <HyreDocsIcon className="w-5 h-5" />
          </div>
          <span className="font-heading font-bold text-lg tracking-tight">HyreDocs</span>
        </Link>
        <div className="hidden md:flex items-center gap-7 text-sm text-slate-600">
          <Link to="/#problem" className="hover:text-slate-900 transition">The Problem</Link>
          <Link to="/#products" className="hover:text-slate-900 transition">Products</Link>
          <Link to="/#bullhorn" className="hover:text-slate-900 transition">Bullhorn</Link>
          <Link to="/pricing" className="hover:text-slate-900 transition">Pricing</Link>
          <Link to="/#faq" className="hover:text-slate-900 transition">FAQ</Link>
        </div>
        <div className="flex items-center gap-3">
          <Link to="/login"><Button variant="ghost">Sign in</Button></Link>
          <a href={DEMO_MAILTO}><Button className="bg-slate-900 hover:bg-slate-800 text-white">Book a demo</Button></a>
        </div>
      </div>
    </nav>
  );
}

function Footer() {
  return (
    <footer className="bg-slate-950 text-slate-400 py-14 mt-20" data-testid="legal-footer">
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
          <Link to="/" className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-[#f97316] to-[#f59e0b] flex items-center justify-center">
              <HyreDocsIcon className="w-5 h-5" />
            </div>
            <span className="font-heading font-bold text-white text-lg">HyreDocs</span>
          </Link>
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm">
            <Link to="/pricing" className="hover:text-white transition">Pricing</Link>
            <Link to="/privacy" className="hover:text-white transition">Privacy</Link>
            <Link to="/cookies" className="hover:text-white transition">Cookies</Link>
            <Link to="/legal/dpa" className="hover:text-white transition">DPA</Link>
            <Link to="/legal/subprocessors" className="hover:text-white transition">Sub-processors</Link>
          </div>
        </div>
        <div className="mt-10 pt-8 border-t border-slate-800 flex flex-wrap justify-between gap-4 text-xs text-slate-500">
          <span>© {new Date().getFullYear()} HyreDocs. Built for recruitment agencies.</span>
          <span>ESIGN · UETA · eIDAS compliant</span>
        </div>
      </div>
    </footer>
  );
}

/** Side navigation for jumping between legal docs. */
function LegalSideNav({ active }) {
  const items = [
    { to: '/privacy', label: 'Privacy Policy' },
    { to: '/cookies', label: 'Cookie Policy' },
    { to: '/legal/dpa', label: 'Data Processing Agreement' },
    { to: '/legal/subprocessors', label: 'Sub-processor List' },
  ];
  return (
    <nav className="sticky top-24 space-y-1 text-sm" aria-label="Legal documents">
      <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-400 mb-3">Legal</p>
      {items.map(it => (
        <Link
          key={it.to}
          to={it.to}
          className={`block px-3 py-2 rounded-md transition ${
            active === it.to
              ? 'bg-slate-900 text-white font-semibold'
              : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100'
          }`}
        >
          {it.label}
        </Link>
      ))}
    </nav>
  );
}

export default function LegalLayout({ title, lastUpdated, activePath, children }) {
  return (
    <div className="min-h-screen bg-white">
      <MarketingNav />
      <div className="max-w-7xl mx-auto px-6 py-12 grid grid-cols-1 md:grid-cols-[220px_1fr] gap-12">
        <aside className="hidden md:block">
          <LegalSideNav active={activePath} />
        </aside>

        <article className="max-w-3xl">
          <header className="mb-10 pb-6 border-b border-slate-200">
            <h1 className="font-heading text-4xl font-bold tracking-tight text-slate-900">{title}</h1>
            {lastUpdated && (
              <p className="mt-2 text-sm text-slate-500">Last updated: {lastUpdated}</p>
            )}
          </header>

          {/* Prose styling — kept on the wrapper so individual pages
              don't need to repeat typography classes everywhere. */}
          <div className="legal-prose text-[15px] leading-relaxed text-slate-700 space-y-5
                          [&_h2]:font-heading [&_h2]:text-2xl [&_h2]:font-bold [&_h2]:text-slate-900 [&_h2]:mt-12 [&_h2]:mb-3
                          [&_h3]:font-heading [&_h3]:text-lg [&_h3]:font-semibold [&_h3]:text-slate-900 [&_h3]:mt-6 [&_h3]:mb-2
                          [&_p]:leading-relaxed
                          [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:space-y-1.5
                          [&_ol]:list-decimal [&_ol]:pl-6 [&_ol]:space-y-1.5
                          [&_a]:text-emerald-700 [&_a]:underline [&_a]:underline-offset-2 [&_a:hover]:text-emerald-800
                          [&_strong]:text-slate-900
                          [&_table]:w-full [&_table]:text-sm [&_table]:border [&_table]:border-slate-200 [&_table]:rounded
                          [&_th]:bg-slate-50 [&_th]:text-left [&_th]:px-3 [&_th]:py-2 [&_th]:font-semibold [&_th]:text-slate-700
                          [&_td]:px-3 [&_td]:py-2 [&_td]:border-t [&_td]:border-slate-100
                          [&_code]:bg-slate-100 [&_code]:px-1.5 [&_code]:py-0.5 [&_code]:rounded [&_code]:text-xs [&_code]:font-mono">
            {children}
          </div>

          <div className="mt-16 pt-8 border-t border-slate-200 text-sm text-slate-500">
            Questions? Email <a href="mailto:privacy@hyredocs.com" className="text-emerald-700 underline">privacy@hyredocs.com</a> — we respond within 30 days, as required by GDPR.
          </div>
        </article>
      </div>
      <Footer />
    </div>
  );
}
